import React, { useEffect, useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { duration, easing } from '../styles/motion'; // [MOTION-A 2026-08-20]

/**
 * App-wide toast service. A small, auto-dismissing "liquid glass" capsule that
 * drops in at the top-center to confirm an action succeeded (or failed), holds
 * for a couple of seconds, then floats up and fades away — so anyone editing
 * data in the app gets immediate, visible feedback.
 *
 * Imperative, like `dialogService`: call from anywhere, no prop threading.
 *
 *   toastSuccess('Website name updated');
 *   toastError('Failed to save change');
 *   toast('Heads up', { kind: 'info' });
 *
 * `<ToastHost />` must be mounted once near the app root (inside ThemeProvider,
 * OUTSIDE any transformed/animated container so `position: fixed` is honoured —
 * it sits next to <DialogHost /> in App.tsx).
 */

export type ToastKind = 'success' | 'error' | 'info';

export interface ToastOptions {
  kind?: ToastKind;
  /** Visible time in ms before the toast auto-dismisses. */
  duration?: number;
}

interface ToastData {
  id: number;
  message: string;
  /** 'progress' = [PROGRESS-TOAST] a "working…" capsule that stays until it is closed */
  kind: ToastKind | 'progress';
  duration: number;
  closing?: boolean;
}

// A single mounted host subscribes; imperative calls push toasts to it.
let pushToast: ((t: ToastData) => void) | null = null;
let patchToast: ((id: number, patch: Partial<ToastData>) => void) | null = null;
let counter = 0;

/* [PROGRESS-TOAST 2026-10-15, user] "when i click on this to run the code then the layout for this should be as it is and the
   loading or processing thing will be show in the top middle area where we get the confirmation or failed thing messages": a
   capsule in the same place that STAYS while the work runs — `toastProgress(text)` shows it (a turning ring), `.update(text)`
   changes its words as the work goes, `.close()` takes it away (the result toast follows it). */
export function toastProgress(message: string): { update: (message: string) => void; close: () => void } {
  const id = ++counter;
  if (pushToast) pushToast({ id, message, kind: 'progress', duration: Infinity });
  return {
    update: (m: string) => { patchToast?.(id, { message: m }); },
    close: () => { patchToast?.(id, { closing: true }); },
  };
}

function enqueue(message: string, options: ToastOptions = {}): void {
  const kind = options.kind ?? 'success';
  // Errors linger a little longer so they're not missed; successes are brief.
  const duration = options.duration ?? (kind === 'error' ? 4200 : 2800);
  const t: ToastData = { id: ++counter, message, kind, duration };
  if (pushToast) {
    pushToast(t);
  } else {
    // Host not mounted (shouldn't happen in-app). Degrade gracefully.
    // eslint-disable-next-line no-console
    console.warn('ToastHost is not mounted — toast skipped:', message);
  }
}

/** Generic toast (defaults to a success/green capsule). */
export function toast(message: string, options?: ToastOptions): void {
  enqueue(message, options);
}
/** Green success capsule. */
export function toastSuccess(message: string, options?: Omit<ToastOptions, 'kind'>): void {
  enqueue(message, { ...options, kind: 'success' });
}
/** Red error capsule (lingers a little longer). */
export function toastError(message: string, options?: Omit<ToastOptions, 'kind'>): void {
  enqueue(message, { ...options, kind: 'error' });
}

/* ── Styling — matches the glass capsules used elsewhere in the app ── */

// [MOTION-A 2026-08-20] Calm, straight slide — no overshoot. The old keyframes settled past
// their resting point (60% at translateY(3px)) which reads as playful; for an audience of
// senior managers the toast should arrive composed and leave quietly. Distances shortened
// too (26px→10px in, 32px→8px out) so the movement registers without drawing the eye.
const slideIn = keyframes`
  from { transform: translateY(-10px); opacity: 0; }
  to   { transform: translateY(0); opacity: 1; }
`;
const slideOut = keyframes`
  from { transform: translateY(0); opacity: 1; }
  to   { transform: translateY(-8px); opacity: 0; }
`;

const Stack = styled.div`
  position: fixed;
  top: 20px;
  left: 0;
  right: 0;
  z-index: 6500; /* above dialogs/modals, tooltips AND sticky headers (all ≤6000) so
                    feedback is never hidden; still below the forced-update wall (7000) */
  display: flex;
  flex-direction: column;
  gap: 10px;
  align-items: center; /* horizontally centered at the top */
  pointer-events: none; /* never block clicks behind the toasts */

  @media (max-width: 600px) {
    top: 12px;
    padding: 0 12px;
  }
`;

/* [MOTION-B 2026-08-20] The confirmation beat for saves that CLOSE their dialog (Edit
   Mapping, Add Payment, Create Deal). Those cannot hold a tick on their own button without
   holding the whole modal open an extra beat purely to be looked at, so the toast's icon
   carries it instead: a quick settle that lands just after the card arrives. Scale + opacity
   only, one small element. */
const iconIn = keyframes`
  from { transform: scale(0.6); opacity: 0; }
  to   { transform: scale(1); opacity: 1; }
`;

/* [PROGRESS-TOAST] the turning ring of a "working…" capsule (a span: the Card's own `i` animation is for the icons) */
const spin = keyframes`
  to { transform: rotate(360deg); }
`;
const Ring = styled.span`
  flex-shrink: 0;
  width: 13px;
  height: 13px;
  border-radius: 50%;
  border: 2px solid rgba(255, 255, 255, 0.22);
  border-top-color: #4acd8b; /* [LIVE-CAPSULE 2026-11-02] the Live capsule's green */
  animation: ${spin} 0.8s linear infinite;

  @media (prefers-reduced-motion: reduce) {
    animation-duration: 2.4s;
  }
`;

/* [LIVE-CAPSULE 2026-11-02, user] The Live capsule (the ColorOS "Live Alerts" idea the user approved: "if something is working in
   the background then the layout of his liquid glass things looks very satisfying"): a "working…" capsule is dark glass in both
   themes with a soft green light that keeps running along its bottom edge while the work goes on (Lead Management's runs, and
   saves waiting in the queue — hooks/useSaveQueue). Downloads keep their own progress (their menus and windows show it). */
const chaseRun = keyframes`
  from { transform: translateX(-110%); }
  to   { transform: translateX(260%); }
`;
const Chase = styled.span`
  position: absolute;
  left: 18px;
  right: 18px;
  bottom: 5px;
  height: 2px;
  border-radius: 2px;
  overflow: hidden;
  background: rgba(255, 255, 255, 0.10);
  pointer-events: none;

  &::after {
    content: '';
    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;
    width: 40%;
    border-radius: 2px;
    background: linear-gradient(90deg, transparent, #4acd8b, transparent);
    box-shadow: 0 0 8px rgba(74, 205, 139, 0.8);
    animation: ${chaseRun} 1.6s cubic-bezier(0.4, 0, 0.2, 1) infinite;
  }

  @media (prefers-reduced-motion: reduce) {
    &::after { animation: none; width: 100%; opacity: 0.55; }
  }
`;

const Card = styled.div<{ $kind: ToastKind | 'progress'; $leaving: boolean }>`
  display: flex;
  align-items: center;
  gap: 9px;
  max-width: 360px;
  padding: 11px 18px;
  /* [LIQUID-GLASS 2026-11-02, user] A glossy glass capsule in the message's colour (styles/liquidGlass.ts): rounder, a
     stronger shine on top, a light top edge and a soft bottom reflection in place of the flat border, and a softer
     colour boost on the blur (the old 180 % amplified whatever sat behind). */
  border-radius: 22px;
  font-size: 13px;
  font-weight: 600;
  line-height: 1.35;
  color: #fff;
  background:
    linear-gradient(180deg, rgba(255,255,255,${(p) => (p.$kind === 'progress' ? '0.14' : '0.30')}) 0%, rgba(255,255,255,0.06) 45%, rgba(255,255,255,0) 60%),
    ${(p) => {
      if (p.$kind === 'error')
        return 'linear-gradient(180deg, rgba(220,38,38,0.88) 0%, rgba(185,28,28,0.94) 100%)';
      // [LIVE-CAPSULE 2026-11-02] the Live capsule: dark glass in both themes (was the blue of an info message)
      if (p.$kind === 'progress')
        return 'linear-gradient(180deg, rgba(34,37,35,0.90) 0%, rgba(14,16,15,0.94) 100%)';
      if (p.$kind === 'info')
        return 'linear-gradient(180deg, rgba(37,99,235,0.88) 0%, rgba(29,78,216,0.94) 100%)';
      // [GRAPHITE-EMERALD 2026-09-28] success: jade in light, emerald in dark (the theme's green600/700).
      return p.theme.colorScheme === 'dark'
        ? 'linear-gradient(180deg, rgba(14,126,78,0.90) 0%, rgba(11,102,64,0.95) 100%)'
        : 'linear-gradient(180deg, rgba(26,127,100,0.90) 0%, rgba(20,107,84,0.95) 100%)';
    }};
  backdrop-filter: blur(20px) saturate(125%);
  -webkit-backdrop-filter: blur(20px) saturate(125%);
  border: none;
  box-shadow:
    0 12px 32px rgba(0, 0, 0, 0.25),
    0 2px 6px rgba(0, 0, 0, 0.18),
    inset 0 1px 0 rgba(255, 255, 255, 0.50),
    inset 0 -1px 0 rgba(255, 255, 255, 0.16),
    inset 0 0 0 1px rgba(255, 255, 255, 0.12);
  /* [MOTION-A 2026-08-20] Enters on the shared entrance curve, leaves on the exit curve —
     the same language as every popup. Shorter than the old 0.36s/0.34s: a notification that
     is still animating when you have already read it feels slow. Removal is driven by
     onAnimationEnd (see ToastItem), so the duration here needs no paired JS timer. */
  animation: ${(p) => (p.$leaving ? slideOut : slideIn)} ${(p) => (p.$leaving ? duration.fast : duration.base)}
    ${(p) => (p.$leaving ? easing.exit : easing.entrance)} forwards;

  /* [LIVE-CAPSULE 2026-11-02] room for the running light along the bottom edge */
  position: relative;
  ${(p) => (p.$kind === 'progress' ? 'padding-bottom: 14px;' : '')}

  i {
    font-size: 13px;
    /* Only on the way IN - while the card is leaving it must fade with the card, not
       re-animate. The fill mode "both" holds the end state so it never flicks back to
       0.6 scale. (No backticks in a styled CSS comment - they end the template.) */
    animation: ${(p) => (p.$leaving ? 'none' : iconIn)} 260ms cubic-bezier(0.16, 1, 0.3, 1) both;
    animation-delay: 60ms;
  }

  @media (prefers-reduced-motion: reduce) {
    i { animation: none; }
  }
`;

const ToastItem: React.FC<{ data: ToastData; onDone: (id: number) => void }> = ({ data, onDone }) => {
  const [leaving, setLeaving] = useState(false);

  // Start the exit animation once the visible window elapses ([PROGRESS-TOAST] none: it leaves when it is closed).
  useEffect(() => {
    if (!Number.isFinite(data.duration)) return undefined;
    const t = window.setTimeout(() => setLeaving(true), data.duration);
    return () => window.clearTimeout(t);
  }, [data.duration]);
  useEffect(() => { if (data.closing) setLeaving(true); }, [data.closing]);

  // onAnimationEnd also fires for the entrance animation — only remove when the
  // exit (leaving) animation has finished.
  const handleAnimationEnd = () => { if (leaving) onDone(data.id); };

  const icon = data.kind === 'error'
    ? 'fa-circle-exclamation'
    : data.kind === 'info'
    ? 'fa-circle-info'
    : 'fa-circle-check';

  return (
    <Card $kind={data.kind} $leaving={leaving} onAnimationEnd={handleAnimationEnd} role="status" data-toast-kind={data.kind}>
      {data.kind === 'progress' ? <Ring aria-hidden="true" /> : <i className={`fas ${icon}`} />}
      <span>{data.message}</span>
      {data.kind === 'progress' && <Chase aria-hidden="true" data-live-chase="" />}
    </Card>
  );
};

export const ToastHost: React.FC = () => {
  const [toasts, setToasts] = useState<ToastData[]>([]);

  useEffect(() => {
    pushToast = (t) =>
      setToasts((prev) => {
        // Cap the stack so a burst of writes can't fill the screen — keep newest.
        // [PROGRESS-TOAST] a "working…" capsule is never pushed out by the cap (only closing ends it).
        const next = [...prev, t];
        if (next.length <= 4) return next;
        const progress = next.filter((x) => x.kind === 'progress' && !x.closing);
        const others = next.filter((x) => !(x.kind === 'progress' && !x.closing));
        const keep = new Set([...progress, ...others.slice(Math.max(0, others.length - Math.max(1, 4 - progress.length)))]);
        return next.filter((x) => keep.has(x));
      });
    patchToast = (id, patch) => setToasts((prev) => prev.map((x) => (x.id === id ? { ...x, ...patch } : x)));
    return () => { pushToast = null; patchToast = null; };
  }, []);

  const remove = (id: number) => setToasts((prev) => prev.filter((t) => t.id !== id));

  if (toasts.length === 0) return null;

  return (
    <Stack>
      {toasts.map((t) => (
        <ToastItem key={t.id} data={t} onDone={remove} />
      ))}
    </Stack>
  );
};
